// Offline job-capture queue (task 3.7 — Req 3.6).
//
// WHERE connectivity is poor, a captured job must not be lost: it is queued and
// posted to the API once a connection returns (Req 3.6). This module holds the
// PURE, framework-free pieces of that queue so they can be unit-tested under
// `node --test` without a DOM or IndexedDB:
//
//   • classifyError  — decide, from a failed POST, whether we are offline (keep &
//                      retry later), hit a transient server error (retry later), or
//                      hit a permanent client error (drop, it will never succeed).
//   • makeQueuedJob  — the stored record shape (id + clientId + payload + createdAt).
//   • createMemoryStore — an in-memory implementation of the store contract, used
//                      by the tests and as the fallback when IndexedDB is absent.
//   • flushQueue     — drain the queue FIFO through a `send` function, applying the
//                      classifyError policy. Pure w.r.t. the store + send it is given.
//
// The browser wiring (opening IndexedDB, listening for the `online` event, calling
// the real API) lives in ../offlineSync.js and builds on these primitives.

// IndexedDB database / object-store identifiers. Bumped only if the record shape
// changes in a way that needs a migration.
export const DB_NAME = 'sparky-offline';
export const DB_VERSION = 1;
export const STORE_NAME = 'job-queue';

// Classify a failed send so flushQueue knows what to do with the queued item:
//   'offline'   — the request never reached the server (network down / no
//                 connectivity). There is no HTTP status. Keep it and retry later.
//   'retryable' — the server answered but the failure is transient (5xx) or an auth
//                 hiccup (401, which the API layer resolves by re-authenticating).
//                 Keep it and retry later so the note is never lost.
//   'permanent' — a 4xx client error: the request is malformed or the target is
//                 gone. Retrying cannot help, so drop it (surfacing the error).
export function classifyError(err) {
  const status = err && typeof err.status === 'number' ? err.status : null;
  if (status == null) return 'offline';
  if (status >= 500) return 'retryable';
  if (status === 401) return 'retryable';
  return 'permanent';
}

// Build the record we persist for a queued job capture. `clientId` is required
// (a job belongs to a client); `payload` is the exact body we will POST to
// `/clients/{clientId}/jobs`. `id` and `createdAt` are injectable for tests and
// default to a unique id and the current time.
export function makeQueuedJob({ clientId, payload } = {}, { id, createdAt } = {}) {
  if (!clientId) throw new Error('clientId is required to queue a job');
  return {
    id: id || makeQueueId(),
    clientId,
    payload: payload || {},
    createdAt: createdAt == null ? Date.now() : createdAt,
  };
}

// A collision-resistant id for a queued item (also its IndexedDB key). Not
// security-sensitive — just needs to be unique within the queue.
export function makeQueueId() {
  const rand = Math.random().toString(36).slice(2, 10);
  return `q_${Date.now().toString(36)}_${rand}`;
}

// Deep clone a plain queue record. Mirrors IndexedDB's structured-clone semantics
// (stored objects are fully detached from the caller's references). Uses the
// platform structuredClone when present, falling back to a JSON round-trip — safe
// here because queue records are plain JSON (ids, strings, numbers, arrays).
function cloneRecord(obj) {
  if (typeof structuredClone === 'function') return structuredClone(obj);
  return JSON.parse(JSON.stringify(obj));
}

// An in-memory store implementing the queue store contract:
//   add(item) -> Promise<item>   getAll() -> Promise<item[]>
//   delete(id) -> Promise        count() -> Promise<number>   clear() -> Promise
// Objects are cloned on the way in and out so callers can't mutate the store's
// copies by reference (mirrors IndexedDB's structured-clone semantics).
export function createMemoryStore(seed = []) {
  let items = seed.map(cloneRecord);
  return {
    async add(item) {
      items.push(cloneRecord(item));
      return cloneRecord(item);
    },
    async getAll() {
      return items.map(cloneRecord);
    },
    async delete(id) {
      items = items.filter((x) => x.id !== id);
    },
    async count() {
      return items.length;
    },
    async clear() {
      items = [];
    },
  };
}

// Drain the queue oldest-first through `send(item)`, which should POST the item
// and resolve on success / reject on failure. Policy per classifyError:
//   • success   -> remove the item, count it as sent.
//   • permanent -> remove the item, record the error (it can never succeed).
//   • otherwise -> stop immediately, leaving this item and the rest queued for a
//                  later attempt (we're offline again or the server is struggling).
// Returns a summary: { sent, dropped, remaining, errors, stopped }.
export async function flushQueue(store, send) {
  const items = (await store.getAll()).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  const result = { sent: 0, dropped: 0, remaining: 0, errors: [], stopped: false };

  for (const item of items) {
    try {
      await send(item);
      await store.delete(item.id);
      result.sent += 1;
    } catch (err) {
      if (classifyError(err) === 'permanent') {
        await store.delete(item.id);
        result.dropped += 1;
        result.errors.push({ id: item.id, message: (err && err.message) || 'Request rejected' });
      } else {
        // Offline or transient: keep this item (and everything after it) and stop.
        result.stopped = true;
        break;
      }
    }
  }

  result.remaining = await store.count();
  return result;
}
